import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ChatAttachmentOptions } from '../../data-contract/chat-attachments';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { chatKeys } from './query-keys';

// Upload capabilities are derived from the caller's tenant role and effectively fixed for the
// session, so a long staleTime keeps the composer from refetching them on every mount.
const CHAT_ATTACHMENT_OPTIONS_STALE_TIME_MS = 300_000;

export const useChatAttachmentOptions = ({
    enabled = true,
}: {
    readonly enabled?: boolean;
} = {}): UseQueryResult<ChatAttachmentOptions, Error> => {
    const { fetchChatAttachmentOptions } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await fetchChatAttachmentOptions();

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: chatKeys.attachmentOptions(),
        staleTime: CHAT_ATTACHMENT_OPTIONS_STALE_TIME_MS,
        // The app-wide composer provider reads these with no error boundary below the app root, and
        // every caller already treats missing options as "not offered", so a failure stays as state.
        throwOnError: false,
    });
};
